import { Stack, Text, Group, Checkbox } from '@mantine/core';
import BigNumber from 'bignumber.js';
import cn from 'clsx';
import { formatDistance } from 'date-fns';
import { ApiOracleInfo, ApiWithdrawalRequest } from 'src/types';
import { formatDecimal } from 'src/utils';

export function ClaimRequest({
  oracleInfo,
  withdrawalDelay,
  request,
  hash,
  isChecked,
  isRedeeming,
  isRedeemed,
  onCheck,
}: {
  oracleInfo: ApiOracleInfo;
  withdrawalDelay: number;
  request: ApiWithdrawalRequest;
  hash?: string;
  isChecked: boolean;
  isRedeeming: boolean;
  isRedeemed: boolean;
  onCheck: () => void;
}) {
  const getLabels = () => {
    if (isRedeemed || request.claimed) {
      return ['Successfully claimed'];
    }

    if (isRedeeming) {
      return ['Claiming...'];
    }

    if (request.is_claimable) {
      return ['Ready to claim'];
    }

    const now = Date.now();

    const epochsUntilUnlock = request.unlock_epoch
      ? new BigNumber(request.unlock_epoch).minus(
          new BigNumber(oracleInfo.latest_update_epoch)
        )
      : new BigNumber(withdrawalDelay).plus(2);

    const projectedUnlock = new BigNumber(
      oracleInfo.latest_update_started_at ?? now
    )
      .plus(epochsUntilUnlock.multipliedBy(oracleInfo.update_interval))
      .times(1000)
      .toNumber();

    const timeLeftHR = formatDistance(
      now,
      now + Math.max(projectedUnlock - now, 0)
    );

    return ['Pending', timeLeftHR];
  };

  const [label, pendingTimeLabel] = getLabels();

  const isDisabled = (!request.is_claimable && !request.claimed) || isRedeemed;

  const renderLabel = (className?: string) => {
    return (
      <Group gap={10} className={cn('max-lg:flex-row-reverse', className)}>
        {pendingTimeLabel && (
          <Text c="var(--mantine-color-main-2)">
            Available in {pendingTimeLabel === 'less than a minute' ? '' : '~'}
            {pendingTimeLabel}
          </Text>
        )}
        <Text
          bg="rgba(154,238,230,0.08)"
          fw={600}
          fz={14}
          className={cn(
            'h-4 px-2 lg:h-7 flex items-center rounded-[8px]',
            '!text-primarybrand',
            'outline outline-solid outline-primarybrand/[0.24] transition-colors',
            isChecked && '!outline-primarybrand'
          )}
        >
          {label}
        </Text>
      </Group>
    );
  };

  return (
    <Group
      key={request.id}
      justify="space-between"
      h={56}
      className={cn(
        'rounded-[16px] px-4 border border-solid',
        request.is_claimable && 'bg-[#151519]',
        isChecked
          ? 'border-secondarybrand bg-[linear-gradient(225deg,rgba(58,236,167,0.08)_6.91%,rgba(154,238,230,0.08)_67.21%)]'
          : 'border-[#202027]',
        isDisabled && 'border-[#202027]'
      )}
    >
      <Checkbox
        label={
          <Stack gap={4}>
            <span>
              {formatDecimal(BigNumber(request.assets).div(1e18), {
                roundingMode: BigNumber.ROUND_FLOOR,
                withTruncate: true,
              })}{' '}
              MON
            </span>
            {renderLabel('lg:hidden')}
          </Stack>
        }
        checked={isChecked}
        size="md"
        fz={18}
        fw={600}
        c={isDisabled ? '#505062' : undefined}
        className={cn(isDisabled && 'pointer-events-none')}
        classNames={{
          body: 'items-center',
          label: 'pl-2',
        }}
        onChange={() => onCheck()}
      />

      {renderLabel('hidden lg:flex')}
    </Group>
  );
}
